import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom'
import { useState, useEffect } from 'react'
import axios from 'axios'
import AuthLayout from './components/layouts/AuthLayout'
import DashboardLayout from './components/layouts/DashboardLayout'
import AdminLayout from './components/layouts/AdminLayout'
import GlobalAdminLayout from './components/layouts/GlobalAdminLayout'
import Login from './pages/auth/Login'
import Register from './pages/auth/Register'
import EmailConfirm from './pages/auth/EmailConfirm'
import ResetPassword from './pages/auth/ResetPassword'
import VerifyLogin from './pages/auth/VerifyLogin'
import Dashboard from './pages/user/Dashboard'
import Profile from './pages/user/Profile'
import Items from './pages/user/Items'
import AddItem from './pages/user/AddItem'
import Tickets from './pages/user/Tickets'
import EscrowTickets from './pages/user/EscrowTickets'
import Notepad from './pages/user/Notepad'
import AdminAccess from './pages/user/AdminAccess'
import Notifications from './pages/user/Notifications'
import UserAdminDashboard from './pages/admin/UserAdminDashboard'
import UserAdminSettings from './pages/admin/UserAdminSettings'
import WebsiteBuilder from './pages/admin/WebsiteBuilder'
import TicketDashboard from './pages/admin/TicketDashboard'
import ContactMessagesPage from './pages/admin/ContactMessagesPage'
import PaymentsOrdersPage from './pages/admin/PaymentsOrdersPage'
import WebsiteReviews from './pages/admin/WebsiteReviews'
import CardGeneratorPage from './pages/admin/CardGeneratorPage'
import CryptoDashboard from './pages/admin/CryptoDashboard'
import WebsiteCustomers from './pages/admin/WebsiteCustomers'
import PublicWebsiteViewer from './pages/public/PublicWebsiteViewer'
import CustomerVerificationRedirect from './pages/public/CustomerVerificationRedirect'
import DomainNotConnected from './pages/public/DomainNotConnected'
import EcommercePage from './pages/public/EcommercePage'
import PaymentConfirmation from './pages/public/PaymentConfirmation'
import GlobalAdminLogin from './pages/global-admin/GlobalAdminLogin'
import GlobalAdminDashboard from './pages/global-admin/GlobalAdminDashboard'
import ProtectedRoute from './components/common/ProtectedRoute'
import PremiumRoute from './components/common/PremiumRoute'
import NotificationSystem from './components/common/NotificationSystem'
import LoadingSpinner from './components/common/LoadingSpinner'
import toast from 'react-hot-toast'
import AdBanner from './components/common/AdBanner'

// Axios base config
const isLocalhost = typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
const defaultApiBase = isLocalhost
  ? 'http://localhost:5000/api'
  : (import.meta.env.VITE_API_BASE_URL || 'https://marketbooksolutionbackendmain.onrender.com/api')

axios.defaults.baseURL = defaultApiBase
axios.defaults.withCredentials = true

function parseHostnames(value) {
  if (!value || typeof value !== 'string') return []
  return value
    .split(',')
    .map((candidate) => candidate.trim().toLowerCase())
    .filter(Boolean)
}

function getFrontendPlatformHosts() {
  const hosts = new Set(parseHostnames(import.meta.env.VITE_PLATFORM_HOSTS || ''))
  const frontendUrl = import.meta.env.VITE_FRONTEND_URL || import.meta.env.VITE_PLATFORM_URL || ''
  if (frontendUrl) {
    try {
      const url = new URL(frontendUrl)
      hosts.add(url.hostname.toLowerCase())
    } catch {
      const cleaned = frontendUrl.trim().replace(/^(https?:\/\/)/, '').split('/')[0].split(':')[0].toLowerCase()
      if (cleaned) hosts.add(cleaned)
    }
  }
  return hosts
}

const DEFAULT_PLATFORM_HOSTNAMES = new Set(['marketbooksolution.com', 'www.marketbooksolution.com', 'localhost', '127.0.0.1'])
const PLATFORM_HOST_SUFFIXES = ['.marketbooksolution.com', '.vercel.app']
const ENV_PLATFORM_HOSTS = getFrontendPlatformHosts()

function isPlatformHost(hostname) {
  if (!hostname || typeof hostname !== 'string') return false
  const normalized = hostname.trim().toLowerCase()
  if (!normalized) return false
  if (DEFAULT_PLATFORM_HOSTNAMES.has(normalized) || ENV_PLATFORM_HOSTS.has(normalized)) {
    return true
  }
  return PLATFORM_HOST_SUFFIXES.some((suffix) => normalized.endsWith(suffix))
}

if (typeof window !== 'undefined') {
  const storedToken = localStorage.getItem('globalAdminToken') || localStorage.getItem('userAdminToken') || localStorage.getItem('token')
  if (storedToken) {
    axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`
  }
}

// Handle expired tokens globally
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const currentPath = window.location.pathname
      // Don't redirect to login for public routes or when accessing via custom domain
      const isPublicRoute = currentPath.startsWith('/store/') ||
                           currentPath === '/' ||
                           currentPath.startsWith('/payment-confirmation')
      const hostname = typeof window !== 'undefined' ? window.location.hostname.toLowerCase() : ''
      const isCustomDomain = hostname !== 'marketbooksolution.com' &&
                            !hostname.endsWith('.marketbooksolution.com')

      if (
        !currentPath.includes('/login') &&
        !currentPath.includes('/register') &&
        !currentPath.includes('/global-admin-login') &&
        !isPublicRoute &&
        !isCustomDomain
      ) {
        localStorage.removeItem('token')
        localStorage.removeItem('globalAdminToken')
        localStorage.removeItem('userAdminToken')
        localStorage.removeItem('userAdminSession')
        delete axios.defaults.headers.common['Authorization']
        toast.error('Session expired. Please login again.')
        window.location.href = '/login'
      }
    }
    return Promise.reject(error)
  }
)

function App() {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [isGlobalAdmin, setIsGlobalAdmin] = useState(false)
  const location = useLocation()

  useEffect(() => {
    checkAuthStatus()
  }, [])

  const checkAuthStatus = async () => {
    try {
      const globalAdminToken = localStorage.getItem('globalAdminToken')
      const userAdminToken = localStorage.getItem('userAdminToken')
      const userToken = localStorage.getItem('token')

      if (globalAdminToken) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${globalAdminToken}`
        await axios.get('/global-admin/profile')
        setIsGlobalAdmin(true)
      } else if (userAdminToken) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${userAdminToken}`
        const response = await axios.get('/auth/profile')
        setUser(response.data.user)
      } else if (userToken) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${userToken}`
        const response = await axios.get('/auth/profile')
        setUser(response.data.user)
      } else {
        localStorage.clear()
      }
    } catch (error) {
      localStorage.clear()
      delete axios.defaults.headers.common['Authorization']
      setUser(null)
      setIsGlobalAdmin(false)
    } finally {
      setLoading(false)
    }
  }

  const login = (userData, token) => {
    setUser(userData)
    localStorage.setItem('token', token)
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`
  }

  const logout = () => {
    setUser(null)
    setIsGlobalAdmin(false)
    localStorage.removeItem('token')
    localStorage.removeItem('globalAdminToken')
    localStorage.removeItem('userAdminToken')
    localStorage.removeItem('userAdminSession')
    // Clear notepad password verification on logout
    sessionStorage.removeItem('notepadPasswordVerified')
    sessionStorage.removeItem('notepadPasswordVerifiedAt')
    delete axios.defaults.headers.common['Authorization']
  }

  // Host-resolved website (when a custom domain points to this SPA)
  const [hostResolvedWebsite, setHostResolvedWebsite] = useState(null)
  const [hostResolvedWebsiteError, setHostResolvedWebsiteError] = useState(null)
  const [hostLookupStatus, setHostLookupStatus] = useState('idle') // 'idle' | 'found' | 'not-found' | 'error'

  // Platform domain constants for exception handling
  const hostname = typeof window !== 'undefined' ? window.location.hostname.toLowerCase() : ''
  const isPlatformDomain = isPlatformHost(hostname)

  // Attempt host-based resolution on app mount so any entry path works
  useEffect(() => {
    let mounted = true

    const resolveHost = async () => {
      // Skip host resolution for authenticated/admin paths
      const adminRoutes = ['/dashboard', '/user-admin', '/card-generator', '/global-admin', '/login', '/register']
      const currentPath = window.location.pathname
      const isAdminRoute = adminRoutes.some(route => currentPath.startsWith(route))
      
      if (isAdminRoute) {
        console.log('[FRONTEND-HOST-RESOLUTION] Skipping host resolution for admin route:', currentPath)
        setHostLookupStatus('idle')
        return
      }

      if (isPlatformDomain) {
        console.log('[FRONTEND-HOST-RESOLUTION] Skipping host resolution for platform domain:', hostname)
        setHostLookupStatus('idle')
        return
      }

      try {
        setHostLookupStatus('idle')
        setHostResolvedWebsiteError(null)
        const currentHost = window.location.hostname.toLowerCase()
        console.log('[FRONTEND-HOST-RESOLUTION] Resolving host:', currentHost, 'isPlatformDomain:', isPlatformDomain)
        const res = await axios.get('/websites-public/by-host', { params: { host: currentHost } })
        console.log('[FRONTEND-HOST-RESOLUTION] Backend response:', res.data)
        if (!mounted) return
        if (res.data && res.data.website) {
          console.log('[FRONTEND-HOST-RESOLUTION] Host resolved to website:', res.data.website.slug)
          setHostResolvedWebsite(res.data.website)
          setHostResolvedWebsiteError(null)
          setHostLookupStatus('found')
          return
        }
        console.log('[FRONTEND-HOST-RESOLUTION] No website found for host')
        setHostResolvedWebsite(null)
        setHostResolvedWebsiteError(null)
        setHostLookupStatus('not-found')
      } catch (err) {
        console.error('[FRONTEND-HOST-RESOLUTION] Error resolving host:', err.response?.data || err.message)
        setHostResolvedWebsite(null)
        const errorMsg = err.response?.data?.message || err.message || 'Failed to load website'
        setHostResolvedWebsiteError(errorMsg)
        if (!isPlatformDomain) {
          setHostLookupStatus('not-found')
        } else {
          setHostLookupStatus('error')
        }
      }
    }

    resolveHost()

    return () => { mounted = false }
  }, [isPlatformDomain])

  const globalAdminLogin = (token) => {
    setIsGlobalAdmin(true)
    localStorage.setItem('globalAdminToken', token)
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`
  }

  const globalAdminLogout = () => {
    setIsGlobalAdmin(false)
    localStorage.removeItem('globalAdminToken')
    if (user) {
      const userToken = localStorage.getItem('token')
      if (userToken) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${userToken}`
      }
    } else {
      delete axios.defaults.headers.common['Authorization']
    }
  }

  // Show spinner while we are determining authentication or resolving a custom domain host
  if (loading || (!isPlatformDomain && hostLookupStatus === 'idle')) return <LoadingSpinner />

  // hide AdBanner on specific routes
  const hiddenAdRoutes = [
    '/login',
    '/register',
    '/global-admin-login',
    '/user-admin',
    '/global-admin',
  ]
  const shouldHideAd = hiddenAdRoutes.some((path) =>
    location.pathname.startsWith(path)
  )

  return (
    <>
      <NotificationSystem />

      <Routes>
        {isGlobalAdmin ? (
          <Route
            path="/*"
            element={
              <GlobalAdminLayout onLogout={globalAdminLogout}>
                <Routes>
                  <Route path="/" element={<GlobalAdminDashboard />} />
                  <Route path="/global-admin/*" element={<GlobalAdminDashboard />} />
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
                {/* ✅ Place AdBanner here at the bottom */}
                {!shouldHideAd && <AdBanner />}
              </GlobalAdminLayout>
            }
          />
        ) : (
          <>
            {/* Auth Routes */}
            <Route
              path="/login"
              element={
                user ? (
                  <Navigate to="/dashboard" replace />
                ) : (
                  <AuthLayout>
                    <Login onLogin={login} />
                  </AuthLayout>
                )
              }
            />
            <Route
              path="/register"
              element={
                user ? (
                  <Navigate to="/dashboard" replace />
                ) : (
                  <AuthLayout>
                    <Register onRegister={login} />
                  </AuthLayout>
                )
              }
            />
            <Route
              path="/global-admin-login"
              element={
                <AuthLayout>
                  <GlobalAdminLogin onLogin={globalAdminLogin} />
                </AuthLayout>
              }
            />
            <Route path="/confirm-email/:token" element={<EmailConfirm />} />
            <Route path="/customer-verify-email/:token" element={<CustomerVerificationRedirect />} />
            <Route path="/reset-password/:token" element={<ResetPassword />} />
            <Route
              path="/verify-login"
              element={
                user ? (
                  <Navigate to="/dashboard" replace />
                ) : (
                  <AuthLayout>
                    <VerifyLogin onLogin={login} />
                  </AuthLayout>
                )
              }
            />

            {/* User Dashboard Routes */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute user={user}>
                  <DashboardLayout user={user} onLogout={logout}>
                    <Dashboard />
                    {!shouldHideAd && <AdBanner />}
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <ProtectedRoute user={user}>
                  <DashboardLayout user={user} onLogout={logout}>
                    <Profile />
                    {!shouldHideAd && <AdBanner />}
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/items"
              element={
                <ProtectedRoute user={user}>
                  <DashboardLayout user={user} onLogout={logout}>
                    <Items />
                    {!shouldHideAd && <AdBanner />}
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/add-item"
              element={
                <ProtectedRoute user={user}>
                  <DashboardLayout user={user} onLogout={logout}>
                    <AddItem />
                    {!shouldHideAd && <AdBanner />}
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/notepad"
              element={
                <ProtectedRoute user={user}>
                  <DashboardLayout user={user} onLogout={logout}>
                    <Notepad user={user} isAdminMode={false} />
                    {!shouldHideAd && <AdBanner />}
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/escrow-tickets"
              element={
                <ProtectedRoute user={user}>
                  <DashboardLayout user={user} onLogout={logout}>
                    <EscrowTickets />
                    {!shouldHideAd && <AdBanner />}
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/tickets"
              element={
                <ProtectedRoute user={user}>
                  <DashboardLayout user={user} onLogout={logout}>
                    <Tickets />
                    {!shouldHideAd && <AdBanner />}
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin-access"
              element={
                <ProtectedRoute user={user}>
                  <DashboardLayout user={user} onLogout={logout}>
                    <AdminAccess />
                    {!shouldHideAd && <AdBanner />}
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/notifications"
              element={
                <ProtectedRoute user={user}>
                  <DashboardLayout user={user} onLogout={logout}>
                    <Notifications />
                    {!shouldHideAd && <AdBanner />}
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />

            {/* User Admin Routes - Protected by Premium Access */}
            <Route
              path="/user-admin"
              element={
                <PremiumRoute user={user}>
                  <AdminLayout user={user} onLogout={logout}>
                    <UserAdminDashboard />
                  </AdminLayout>
                </PremiumRoute>
              }
            />
            <Route
              path="/user-admin/websites"
              element={
                <PremiumRoute user={user}>
                  <AdminLayout user={user} onLogout={logout}>
                    <WebsiteBuilder />
                  </AdminLayout>
                </PremiumRoute>
              }
            />
            <Route
              path="/website-builder"
              element={
                <PremiumRoute user={user}>
                  <AdminLayout user={user} onLogout={logout}>
                    <WebsiteBuilder />
                  </AdminLayout>
                </PremiumRoute>
              }
            />
            <Route
              path="/website-builder/:websiteId"
              element={
                <PremiumRoute user={user}>
                  <AdminLayout user={user} onLogout={logout}>
                    <WebsiteBuilder />
                  </AdminLayout>
                </PremiumRoute>
              }
            />
            <Route
              path="/tickets/:websiteId"
              element={
                <PremiumRoute user={user}>
                  <AdminLayout user={user} onLogout={logout}>
                    <TicketDashboard />
                  </AdminLayout>
                </PremiumRoute>
              }
            />
            <Route
              path="/contact-messages/:websiteId"
              element={
                <PremiumRoute user={user}>
                  <AdminLayout user={user} onLogout={logout}>
                    <ContactMessagesPage />
                  </AdminLayout>
                </PremiumRoute>
              }
            />
            <Route
              path="/reviews/:websiteId"
              element={
                <PremiumRoute user={user}>
                  <AdminLayout user={user} onLogout={logout}>
                    <WebsiteReviews />
                  </AdminLayout>
                </PremiumRoute>
              }
            />
            <Route
              path="/customers/:websiteId"
              element={
                <PremiumRoute user={user}>
                  <AdminLayout user={user} onLogout={logout}>
                    <WebsiteCustomers />
                  </AdminLayout>
                </PremiumRoute>
              }
            />
            <Route
              path="/payments-orders/:websiteId"
              element={
                <PremiumRoute user={user}>
                  <AdminLayout user={user} onLogout={logout}>
                    <PaymentsOrdersPage />
                  </AdminLayout>
                </PremiumRoute>
              }
            />
            <Route
              path="/card-generator/:websiteId"
              element={
                <PremiumRoute user={user}>
                  <AdminLayout user={user} onLogout={logout}>
                    <CardGeneratorPage />
                  </AdminLayout>
                </PremiumRoute>
              }
            />
            <Route
              path="/crypto-transactions/:websiteId"
              element={
                <PremiumRoute user={user}>
                  <AdminLayout user={user} onLogout={logout}>
                    <CryptoDashboard />
                  </AdminLayout>
                </PremiumRoute>
              }
            />
            <Route
              path="/user-admin/notepad"
              element={
                <PremiumRoute user={user}>
                  <AdminLayout user={user} onLogout={logout}>
                    <Notepad user={user} isAdminMode={true} />
                  </AdminLayout>
                </PremiumRoute>
              }
            />
            <Route
              path="/user-admin/settings"
              element={
                <PremiumRoute user={user}>
                  <AdminLayout user={user} onLogout={logout}>
                    <UserAdminSettings />
                  </AdminLayout>
                </PremiumRoute>
              }
            />

            {/* Public Website Routes */}
            <Route
              path="/store/:slug/verify/:verificationId"
              element={<PublicWebsiteViewer />}
            />
            <Route
              path="/store/:slug"
              element={<PublicWebsiteViewer />}
            />
            <Route
              path="/store/:slug/shop"
              element={<EcommercePage />}
            />
            <Route
              path="/payment-confirmation"
              element={<PaymentConfirmation />}
            />

            {/* Redirects and host-based resolution for custom domains */}
            <Route
              path="/"
              element={
                // if host mapped to site, render it
                hostResolvedWebsite ? (
                  <PublicWebsiteViewer prefetchedWebsite={hostResolvedWebsite} prefetchedWebsiteError={hostResolvedWebsiteError} />
                ) :
                // if non-platform domain and lookup finished with error/not-found, show domain error or network error
                (!isPlatformDomain && (hostLookupStatus === 'not-found' || hostLookupStatus === 'error')) ? (
                  <PublicWebsiteViewer prefetchedWebsite={null} prefetchedWebsiteError={hostResolvedWebsiteError} />
                ) :
                // otherwise fall back to normal platform routing
                user ? <Navigate to="/dashboard" replace /> : <Navigate to="/login" replace />
              }
            />
            {/* If host resolves to a website, render it for any unmatched entry path without changing the URL */}
            <Route
              path="/*"
              element={
                hostResolvedWebsite ? (
                  <PublicWebsiteViewer prefetchedWebsite={hostResolvedWebsite} prefetchedWebsiteError={hostResolvedWebsiteError} />
                ) : (!isPlatformDomain && (hostLookupStatus === 'not-found' || hostLookupStatus === 'error')) ? (
                  <PublicWebsiteViewer prefetchedWebsite={null} prefetchedWebsiteError={hostResolvedWebsiteError} />
                ) : (
                  <Navigate to="/" replace />
                )
              }
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </>
        )}
      </Routes>
    </>
  )
}

export default App


